import React, { useState, useEffect } from "react";
import PlanTable from "../../components/ui/planTable/PlanTable";
import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner";
import Developers from "../Developers/Developers";
import OurCustomers from "..//ourCustomers/ourCustomers";

const MainPage = () => {
  const [products, setProducts] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [virtualServers, setVirtualServers] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [expandedIndex, setExpandedIndex] = useState(null); // State for FAQ

  // Fetch web hosting products
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(
          "https://api.spadserver.com/api/product/iran-web-hosting"
        );
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        const result = await response.json();
        setProducts(result.data.products);
      } catch (error) {
        setError(`Error fetching data: ${error.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Fetch virtual server products
  useEffect(() => {
    const fetchVirtualServers = async () => {
      try {
        const response = await fetch(
          "https://api.spadserver.com/api/product/iran-virtual-server"
        );
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        const result = await response.json();
        setVirtualServers(result.data.products);
      } catch (error) {
        setError(`Error fetching virtual server data: ${error.message}`);
      }
    };

    fetchVirtualServers();
  }, []);

  const handleSearch = async (event) => {
    event.preventDefault();
    setSearchLoading(true);
    setShowSearchResults(true);

    try {
      const response = await fetch(
        `https://api.spadserver.com/api/domain-search?domain=${searchTerm}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
        }
      );
      const data = await response.json();
      if (data && data.other) {
        setSearchResults(data.other);
      } else {
        setSearchResults([]);
      }
    } catch (error) {
      setError(`Error fetching search results: ${error.message}`);
    } finally {
      setSearchLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return <div className="text-red-500 text-center">Error: {error}</div>;
  }

  // Select Plan 1 and Plan 2
  const plan1 = products.find((product) => product.name === "HOSTIRL1");
  const plan2 = products.find((product) => product.name === "HOSTIRL2");

  const toggleFAQ = (index) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  return (
    <div className="container mx-auto p-4">
      {/* Top Section with GIF and Link */}
      <div className="flex flex-col md:flex-row gap-4 mb-8">
        {/* Left Column with GIF and Link */}
        <div className="flex-1 p-4 rounded">
          <img
            src="images/servers.gif"
            alt="Server GIF"
            className="mb-4 w-full h-auto"
          />
        </div>

        {/* Right Column with Hosting Plans */}
        <div className="flex-1 p-4 rounded mt-4 md:mt-0">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {plan1 && (
              <div className="p-2 sm:p-4 rounded flex justify-center text-white">
                <PlanTable products={[plan1]} />
              </div>
            )}
            {plan2 && (
              <div className="p-2 sm:p-4 rounded flex justify-center text-white">
                <PlanTable products={[plan2]} />
              </div>
            )}
          </div>
          <div className="text-left mt-4">
            <a
              href="product/iran-web-hosting"
              className="text-white hover:underline border-b border-white border-1"
              target="_blank"
              rel="noopener noreferrer"
            >
              مشاهده بیشتر
            </a>
          </div>
        </div>
      </div>

      {/* Domain Search Input */}
      <div
        className="p-4 rounded mb-8 flex flex-col lg:flex-row justify-between items-center rounded-xl"
        style={{ backgroundColor: "rgba(128, 128, 128, 0.3)" }}
      >
        <div className="flex flex-col w-full lg:w-auto mb-4 lg:mb-0">
          <div className="text-white text-2xl mb-4">
            بهترین نام دامنه را برای وب سایت خود پیدا کنید.
          </div>
          <div className="text-[14px] mt-4 text-white">
            آیا دامنه دارید؟{" "}
            <a
              href="https://crm.spadserver.com/cart.php?a=add&domain=transfer"
              className="text-blue-500 hover:underline"
            >
              می‌توانید دامنه را انتقال دهید.
            </a>
            <div className="border-t border-gray-300 mt-2"></div>
          </div>
        </div>

        <form
          onSubmit={handleSearch}
          className="flex flex-col sm:flex-row w-full gap-2 sm:gap-0 lg:w-auto lg:ml-4"
        >
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="جستجوی دامنه..."
            className="p-2 border-gray-300 text-black focus:outline-none focus:ring-2 focus:ring-blue-500 md:rounded-r-md md:rounded-l-none sm:rounded-md flex-1 w-full sm:w-auto max-w-xs lg:max-w-md"
          />
          <button
            type="submit"
            className="p-2 bg-blue-500 text-white md:rounded-r-none md:rounded-l-md sm:rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            جستجو
          </button>
        </form>
      </div>

      {/* Domain Search Results */}
      {showSearchResults && (
        <div className="p-4 rounded">
          {searchLoading ? (
            <div className="flex justify-center items-center">
              <LoadingSpinner />
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {searchResults.map((domain, index) => (
                <div
                  key={index}
                  className="relative p-2 sm:p-4 border rounded-lg hover:bg-gray-600 transition duration-300 ease-in-out"
                >
                  <div className="domain-content text-center">
                    <h2 className="domain-text-search text-lg sm:text-xl font-semibold mb-2 text-white hover:text-green-400">
                      دامنه {domain.tls}
                    </h2>
                    <div className="annually-search text-white mb-2">
                      (سالانه)
                    </div>
                    <div className="bottom-content flex flex-col sm:flex-row justify-between items-center">
                      <p
                        className={`mt-2 font-bold text-sm mb-4 ${
                          domain.isAvailable ? "text-green-400" : "text-red-400"
                        }`}
                      >
                        {domain.isAvailable ? "در دسترس" : "غیر قابل دسترس"}
                      </p>
                      {domain.isAvailable && (
                        <a
                          href={`https://crm.spadserver.com/cart.php?a=add&domain=${searchTerm}.${domain.tls}`}
                          className="purchase-button bg-black mt-4 block text-center py-2 px-4 rounded hover:bg-gray-800"
                        >
                          خرید دامنه
                        </a>
                      )}
                    </div>
                  </div>
                  <div className="domain-description mt-2 text-gray-300 text-sm">
                    <p>
                      Availability:{" "}
                      {domain.isAvailable ? "در دسترس" : "غیر قابل دسترس"}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Virtual Servers Section */}
      <div className="server-fetch p-4 rounded mt-12">
        <div className="text-center text-white text-2xl font-bold">
          سرورهای مجازی
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
          {virtualServers.slice(0, 4).map((server) => (
            <div
              key={server.id}
              className="p-2 sm:p-3 rounded flex justify-center text-white"
            >
              <PlanTable products={[server]} />
            </div>
          ))}
        </div>

        <div
          className="wrapper p-4 mt-24 rounded-xl"
          style={{ backgroundColor: "rgba(128, 128, 128, 0.3)" }}
        >
          <div className="title-spad text-center text-white text-2xl font-bold mt-6">
            چرا اسپاد سرور را انتخاب می کنید؟
          </div>

          <div className="text-center mt-12 text-white">
            ما از آخرین فن آوری سرورهای HP در بهترین دیتاسنترهای ایران و جهان
            استفاده می کنیم تا اطمینان حاصل کنیم که مشتریان ما بهترین عملکرد را
            از وب سایت های خود دریافت می کنند.
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-20">
            <div className="flex flex-col justify-center items-center rounded-full">
              <div className="main-icon text-white rounded-full p-2 w-16 h-16 flex justify-center items-center">
                <img
                  className="w-full h-full object-contain"
                  src="/images/stack-icon.png"
                  alt="Icon 1"
                />
              </div>
              <p className="mt-4 text-white">فناوری پایدار و ایمن</p>
            </div>
            <div className="flex flex-col justify-center items-center">
              <div className="main-icon text-white rounded-full p-2 w-16 h-16 flex justify-center items-center">
                <img
                  className="w-full h-full object-contain"
                  src="/images/headphone.png"
                  alt="Icon 2"
                />
              </div>

              <p className="mt-4 text-white">پشتیبانی 24 ساعته</p>
            </div>
            <div className="flex flex-col justify-center items-center">
              <div className="main-icon text-white rounded-full p-2 w-16 h-16 flex justify-center items-center">
                <img
                  className="w-full h-full object-contain"
                  src="/images/server-storage.png"
                  alt="Icon 3"
                />
              </div>
              <p className="mt-4 text-white">زیر ساخت اختصاصی</p>
            </div>
            <div className="flex flex-col justify-center items-center">
              <div className="main-icon text-white rounded-full p-2 w-16 h-16 flex justify-center items-center">
                <img
                  className="w-full h-full object-contain"
                  src="/images/star.png"
                  alt="Icon 4"
                />
              </div>

              <p className="mt-4 text-white">سرویس مهاجرت رایگان</p>
            </div>
          </div>
        </div>

        <div className="mainPageDevelopers mt-24 mb-14">
          <Developers />
        </div>
      </div>

      <div className="mainPageCustomers mb-14">
        <OurCustomers />
      </div>

      <div
        className="container mx-auto p-4 rounded-xl"
        style={{ backgroundColor: "rgba(128, 128, 128, 0.3)" }}
      >
        {/* Header */}
        <div className="text-center text-white text-2xl font-bold py-4">
          سوالات شما
        </div>

        {/* FAQ Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-8">
          {/* Right Side (Options) */}
          <div className="md:col-span-1 flex flex-col space-y-4">
            <div className="border p-4 rounded-lg flex items-center space-x-4">
              <img src="images/database.png" alt="Icon" className="p-6" />
              <div className="text-white">
                <h4 className="text-lg font-semibold">سرور مجازی</h4>
                <p className="text-sm">سرور مجازی ایران/خارج</p>
              </div>
            </div>

            <div className="border p-4 rounded-lg flex items-center space-x-4">
              <img src="images/hosting.png" alt="Icon" className="p-6" />
              <div className="text-white">
                <h4 className="text-lg font-semibold">هاست</h4>
                <p className="text-sm">سرور مجازی ایران/خارج</p>
              </div>
            </div>

            <div className="border p-4 rounded-lg flex items-center space-x-4">
              <img src="images/ip.png" alt="Icon" className="p-6" />
              <div className="text-white">
                <h4 className="text-lg font-semibold">IP</h4>
                <p className="text-sm">سرور مجازی ایران/خارج</p>
              </div>
            </div>
          </div>

          {/* Left Side (Main Content) */}
          <div className="md:col-span-1 lg:col-span-3 p-6 rounded-lg flex flex-col space-y-4">
            <div className="border border-gray-500 rounded-lg p-4">
              <h3
                className="text-white md:text-lg font-semibold flex items-center cursor-pointer sm:text-sm"
                onClick={() => toggleFAQ(0)}
              >
                <span className="mr-2">
                  {expandedIndex === 0 ? "➖" : "➕"}
                </span>
                <p className="pr-4">
                  آیا امکان خرید سرویس با پلنی‌های متفاوت از پلن‌های درج شده در
                  سایت وجود دارد؟
                </p>
              </h3>
              {expandedIndex === 0 && (
                <p className="text-gray-300 mt-2">
                  بله، برای این منظور لطفا با ما تماس بگیرید تا نیازهای شما را
                  به بهترین شکل پاسخ دهیم.
                </p>
              )}
            </div>

            <div className="border border-gray-500 rounded-lg p-4">
              <h3
                className="text-white md:text-lg font-semibold flex items-center cursor-pointer sm:text-sm"
                onClick={() => toggleFAQ(1)}
              >
                <span className="mr-2">
                  {expandedIndex === 1 ? "➖" : "➕"}
                </span>

                <p className="pr-4">
                  آیا امکان خرید سرویس با پلنی‌های متفاوت از پلن‌های درج شده در
                  سایت وجود دارد؟
                </p>
              </h3>
              {expandedIndex === 1 && (
                <p className="text-gray-300 mt-2">
                  بله، برای این منظور لطفا با ما تماس بگیرید تا نیازهای شما را
                  به بهترین شکل پاسخ دهیم.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MainPage;
